import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import { IoMenuOutline, IoCloseOutline, IoPersonOutline } from "react-icons/io5";
import { useAuth } from "../../auth/AuthProvider";

const navigation = [
  { name: "HOME", path: "hero" },
  { name: "SERVICES", path: "services" },
  { name: "ABOUT US", path: "about" },
  { name: "CONTACT US", path: "contact" },
];

const PublicHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHomePage = location.pathname === "/";

  const handleNavClick = (path) => {
    if (!isHomePage) {
      // If not on homepage, navigate to home first
      navigate("/", { state: { scrollTo: path } });
    }
  };

  const renderNavLink = ({ name, path }) => {
    if (isHomePage) {
      return (
        <ScrollLink
          key={path}
          to={path}
          smooth={true}
          duration={300}
          offset={-60}
          className="cursor-pointer inline-flex items-center px-1 pt-1 text-base font-medium text-primary-light hover:text-primary border-transparent hover:border-secondary transition-colors duration-200"
          onClick={() => setIsMenuOpen(false)}
        >
          {name}
        </ScrollLink>
      );
    }

    return (
      <button
        key={path}
        onClick={() => {
          handleNavClick(path);
          setIsMenuOpen(false);
        }}
        className="inline-flex items-center px-1 pt-1 text-base font-medium text-primary-light hover:text-primary border-transparent hover:border-secondary transition-colors duration-200"
      >
        {name}
      </button>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="cursor-pointer">
              <img
                src="/img/impact.webp"
                alt="Impact Logistics"
                className="w-32 h-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navigation.map((item) => renderNavLink(item))}
          </nav>

          <div className="flex lg:flex-row flex-col justify-center items-center">
            {/* Track Package Button */}
            <div className="hidden md:flex items-center">
              <Link
                to="/tracking"
                className="ml-8 items-center justify-center flex px-6 py-2.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary-dark hover:bg-primary-dark transition-all duration-200 shadow-[4px_10px_20px_0px_#5C28D44D]"
              >
                Track a Package
              </Link>
            </div>
            {/* Account button show when user login */}
            {user && (
              <div className="hidden md:flex items-center">
                <Link
                  to="/user/home"
                  className="ml-4 items-center justify-center flex px-4 py-2 text-sm font-medium text-primary-dark hover:text-primary border border-primary-dark rounded-md hover:bg-primary-dark/5 transition-all duration-200"
                >
                  <IoPersonOutline className="w-5 h-5 mr-2" />
                  <span>Account</span>
                </Link>
              </div>
            )}
          </div>
          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="items-center justify-center flex p-2 rounded-md text-primary hover:text-primary-dark hover:bg-secondary/20"
            >
              {isMenuOpen ? (
                <IoCloseOutline className="block h-6 w-6" />
              ) : (
                <IoMenuOutline className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-secondary">
          <div className="pt-2 pb-3 space-y-1 bg-white">
            {navigation.map((item) => (
              <div key={item.path} className="block pl-3 pr-4 py-2">
                {renderNavLink({
                  ...item,
                  className:
                    "text-base font-medium text-secondary-dark hover:text-primary hover:bg-secondary/10 border-transparent cursor-pointer",
                })}
              </div>
            ))}
            <Link
              to="/tracking"
              className="block mx-3 text-center px-4 py-2 my-2 text-sm font-medium text-white bg-primary-dark hover:bg-primary-dark rounded-md transition-all duration-200 shadow-[0_0_20px_rgba(239,68,68,0.4)] hover:shadow-[0_0_25px_rgba(239,68,68,0.6)] active:shadow-[0_0_15px_rgba(239,68,68,0.3)]"
              onClick={() => setIsMenuOpen(false)}
            >
              Track a Package
            </Link>
            {user && (
              <div className="items-center">
                <Link
                  to="/user/home"
                  className="ml-4 items-center justify-center flex px-4 py-2 text-sm font-medium text-primary-dark hover:text-primary border border-primary-dark rounded-md hover:bg-primary-dark/5 transition-all duration-200"
                >
                  <IoPersonOutline className="w-5 h-5 mr-2" />
                  <span>Account</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
